// eslint-disable-next-line import-x/no-restricted-paths
import { OdysseyAnalyticsBridge } from '@odyssey/analytics/OdysseyAnalyticsBridge'

export interface VoiceAnalyticsCallQualityPayload {
  id: string
  sid: string | null
  roomId: string
  endedAt?: number | null
  metrics: VoiceAnalyticsCallQualityMetrics
}

export interface VoiceAnalyticsCallQualityMetrics {
  platform: 'web' | 'desktop'
  subPlatform?: NodeJS.Platform
  bandwidthEstimate?: number
  jitter?: number
  mos?: number
  packetsLostPercentage?: number
  rtt?: number
  bitrateType: 'fixed' | 'variable'
}

export default class VoiceAnalyticsStore {
  private readonly analytics: typeof OdysseyAnalyticsBridge

  constructor() {
    this.analytics = OdysseyAnalyticsBridge
  }

  /**
   * Tracks when participants are added to the call by the user
   *
   * @param addedCount number of participants that were added
   * @param oldCount number of active participants in the call before the new participants were added
   * @param newCount number of active participants in the call after the new participants were added
   * @param mode represents how the participants were added
   * @param messageLength length of the add/transfer message invite (0 if no message was added to the invite)
   * @param targets array that represents the participants that were added to the call
   */
  callParticipantsAdded(
    addedCount: number,
    oldCount: number,
    newCount: number,
    mode: ('add' | 'add-listener' | 'warm-transfer')[],
    messageLength: number,
    targets: ('number' | 'contact' | 'inbox' | 'inbox-member' | 'member')[],
  ) {
    void this.analytics.track('call_participants_added', {
      addedCount,
      oldCount,
      newCount,
      mode: mode.join(','),
      messageLength,
      targets: targets.join(','),
    })
  }

  /**
   * Tracks when a participant is removed from the call by the user
   *
   * @param oldCount number of active participants in the call before the participant was removed
   * @param newCount number of active participants in the call after the participant was removed
   * @param target string that represents the type of the participant that was removed from the call
   */
  callParticipantRemoved(
    oldCount: number,
    newCount: number,
    target: 'number' | 'contact' | 'inbox' | 'member',
  ) {
    void this.analytics.track('call_participant_removed', {
      oldCount,
      newCount,
      target,
    })
  }

  /**
   * Tracks when an incoming call is received and the user takes action
   *
   * @param phoneNumberId ID of the inbox phone number that received the call
   * @param handled how the call was handled: 'accepted', 'rejected', or 'missed'
   * @param type how the caller was identified
   */
  incomingCallReceived(
    phoneNumberId: string,
    handled: 'accepted' | 'rejected' | 'missed',
    type:
      | 'potential spam'
      | 'caller ID'
      | 'no caller ID'
      | 'saved contact'
      | 'AI contact suggestion',
  ) {
    void this.analytics.track('incoming_call_received', { phoneNumberId, handled, type })
  }

  /**
   * Tracks when the user toggles hold for a specific participant
   *
   * @param value whether or not hold was toggled on for the participant
   * @param target string that represents the type of the participant for whom hold was toggled
   */
  callParticipantHoldToggled(value: boolean, target: 'number' | 'contact') {
    void this.analytics.track('call_participant_hold_toggled', { value, target })
  }

  /**
   * Tracks the maximum number of concurrent active calls
   *
   * @param userId the ID of the user
   * @param phoneNumber the phone number
   * @param numberOfConcurrentCalls the number of concurrent active calls
   * @param startedAt the ISO timestamp when the call was accepted
   */
  maxConcurrentCallsTracked(
    userId: string,
    phoneNumber: string,
    numberOfConcurrentCalls: number,
    startedAt: string,
  ) {
    void this.analytics.track('max_concurrent_calls', {
      userId,
      phoneNumber,
      numberOfConcurrentCalls,
      startedAt,
    })
  }

  /**
   * Tracks when the call quality feedback modal rating was submitted.
   * @param payload the payload of the call quality feedback rating.
   */
  callQualityRated({
    rating,
    call,
  }: {
    rating: 'good' | 'bad'
    call: VoiceAnalyticsCallQualityPayload
  }) {
    void this.analytics.track('call_quality_rated', {
      callId: call.id,
      callSid: call.sid,
      roomId: call.roomId,
      rating,
    })
  }

  /**
   * Tacks when the call quality feedback reason was given.
   * @param payload the payload of the call quality feedback reason.
   */
  callQualityIssueGiven({
    issue,
    issueText,
    call,
  }: {
    issue: string
    issueText?: string
    call: VoiceAnalyticsCallQualityPayload
  }) {
    void this.analytics.track('call_quality_reason_given', {
      callId: call.id,
      callSid: call.sid,
      ratingReason: issue,
      roomId: call.roomId,
    })
    if (issueText) {
      void this.analytics.track('call_quality_feedback_given', {
        callId: call.id,
        callSid: call.sid,
        ratingFeedback: issueText,
        roomId: call.roomId,
      })
    }
  }

  /**
   * Tracks when a user opens the call metrics panel from the options menu
   */
  callMetricsPanelOpenedFromOptions() {
    void this.analytics.track('call_metrics_panel_opened_from_options')
  }

  /**
   * Tracks when a user opens the call metrics panel from the network status icon
   * in the call header
   */
  callMetricsPanelOpenedFromNetworkIcon() {
    void this.analytics.track('call_metrics_panel_opened_from_network_icon')
  }

  /**
   * Tracks when poor call quality is detected and conveys that to the user
   * with a callout in the call modal
   *
   * @param callProgress The current time of the call. It will be null if
   * the call progress can't be determined. Measured in milliseconds.
   * @param callId The call id
   */
  callQualityDropDetected(callProgress: number | null, callId: string) {
    void this.analytics.track('call_quality_drop_detected', {
      callProgress,
      callId,
    })
  }

  /**
   * Tracks when a user opens the call metrics panel from the call quality
   * drop callout
   */
  callMetricsPanelOpenedFromQualityDropCallout() {
    void this.analytics.track('call_metrics_panel_opened_from_quality_drop_callout')
  }

  /**
   * Tracks when an incoming call is suppressed because the user is at max calls on hold limit
   *
   * @param phoneNumberId the ID of the phone number receiving the call
   */
  incomingCallSuppressed(phoneNumberId: string) {
    void this.analytics.track('incoming_call_suppressed', { phoneNumberId })
  }
}
